import { motion } from "framer-motion";
import {
  ChatBubbleLeftRightIcon,
  UserGroupIcon,
  CurrencyDollarIcon,
  DocumentTextIcon,
  BoltIcon,
  GlobeAltIcon,
  ChartBarIcon,
  RocketLaunchIcon,
} from "@heroicons/react/24/outline";

const howItWorks = [
  {
    title: "1. Describe Your Idea",
    description: "Share your product concept directly with our Telegram bot.",
    icon: ChatBubbleLeftRightIcon,
  },
  {
    title: "2. Choose Audience Size",
    description: "Pick how many simulated Web3 users you want.",
    icon: UserGroupIcon,
  },
  {
    title: "3. Pay in ETH",
    description:
      "Send a small ETH amount on Base to trigger the process.",
    icon: CurrencyDollarIcon,
  },
  {
    title: "4. Receive Your Report",
    description: "Get a PDF report with diverse Web3 Users feedback powered by AI",
    icon: DocumentTextIcon,
  },
];

const benefits = [
  {
    title: "Lightning Fast",
    description: "No waitlists or forms. Just message and go.",
    icon: BoltIcon,
  },
  {
    title: "AI + Web3 Native",
    description: "Simulates real-world Web3 personas.",
    icon: GlobeAltIcon,
  },
  {
    title: "Actionable Reports",
    description: "Clean PDFs you can use or pitch instantly.",
    icon: ChartBarIcon,
  },
  {
    title: "On-Chain Payments",
    description: "Lightweight ETH flow on Base testnet.",
    icon: RocketLaunchIcon,
  },
];

const Features = () => {
  return (
    <div className="space-y-32  bg-black relative z-0">

      {/* Benefits Section */}
      <section className="section-padding">
        <div className="container-custom">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
            className="text-center mb-16"
          >
            <h2 className="text-3xl md:text-4xl font-bold mb-4 text-white">
              Why InsightSwarm?
            </h2>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {benefits.map((benefit, index) => (
              <motion.div
                key={benefit.title}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.8, delay: index * 0.2 }}
                className="p-8 rounded-xl text-center"
              >
                <benefit.icon className="w-12 h-12 text-white mb-6 mx-auto" />
                <h3 className="text-xl font-semibold mb-4 text-white">
                  {benefit.title}
                </h3>
                <p className="text-gray-400">{benefit.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>
      {/* How It Works Section */}
      <section className="section-padding">
        <div className="container-custom">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
            className="text-center mb-16"
          >
            <h2 className="text-3xl md:text-4xl font-bold mb-4 text-white">
              How It Works
            </h2>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {howItWorks.map((step, index) => (
              <motion.div
                key={step.title}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.8, delay: index * 0.2 }}
                className="p-8 rounded-xl text-center"
              >
                <step.icon className="w-12 h-12 text-white mb-6 mx-auto" />
                <h3 className="text-xl font-semibold mb-4 text-white">
                  {step.title}
                </h3>
                <p className="text-gray-400">{step.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>


      {/* Use Cases Section */}
      <section className="section-padding bg-black">
        <div className="container-custom">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
            className="text-center mb-16"
          >
            <h2 className="text-3xl md:text-4xl font-bold mb-4 text-white">
              Who It's For
            </h2>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-12">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8 }}
              className="flex flex-col items-center text-center"
            >
              <BoltIcon className="w-12 h-12 text-white mb-4" />
              <h3 className="text-xl font-bold text-white mb-2">
                Testing product-market fit for new Web3 dApps
              </h3>
              <p className="text-gray-400 text-base"></p>
            </motion.div>
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8, delay: 0.2 }}
              className="flex flex-col items-center text-center"
            >
              <GlobeAltIcon className="w-12 h-12 text-white mb-4" />
              <h3 className="text-xl font-bold text-white mb-2">
                Validating DeFi, NFT, or DAO ideas
              </h3>
              <p className="text-gray-400 text-base"></p>
            </motion.div>
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8, delay: 0.4 }}
              className="flex flex-col items-center text-center"
            >
              <ChartBarIcon className="w-12 h-12 text-white mb-4" />
              <h3 className="text-xl font-bold text-white mb-2">
                Getting pre-demo day or investor-friendly validation
              </h3>
              <p className="text-gray-400 text-base"></p>
            </motion.div>
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8, delay: 0.6 }}
              className="flex flex-col items-center text-center"
            >
              <RocketLaunchIcon className="w-12 h-12 text-white mb-4" />
              <h3 className="text-xl font-bold text-white mb-2">
                Saving time on expensive surveys or outdated methods
              </h3>
              <p className="text-gray-400 text-base"></p>
            </motion.div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="section-padding bg-black">
        <div className="container-custom text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
          >
            <h2 className="text-3xl font-bold mb-4 text-white">
              Built for Builders
            </h2>
            <p className="text-gray-400 text-lg mb-8 max-w-2xl mx-auto">
              Whether you're validating a DeFi idea, NFT concept, or DAO flow
              InsightSwarm delivers feedback you can act on.
            </p>
            <a
              href="https://t.me/InsightSwarmbot"
              target="_blank"
              rel="noopener noreferrer"
              className="bg-white text-black px-6 py-3 rounded-full hover:bg-gray-200 transition-all duration-300 inline-block"
            >
              Start Now in Telegram
            </a>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default Features;
